import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TableChip } from '../components/EnhancedTableToolbar';
import { DEFAULT_BUSINESS_START_HOUR, transactionFeedbackOptions } from '../constants';
import { dateRange, DateRangeConfig, DayOfWeek, getLabelForPeriod, isPresetPeriod } from '../helpers/dateUtils';

import { EN_GB } from '../helpers/helpers';
import useSavedField from '../helpers/useSavedField';
import { TransactionReportFilters } from '../models/transactions';
import ValidationError from '../models/validationError';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';
import { SettingsContext } from './SettingsContext';
import { UserContext } from './UserContext';


const defaultFiltersDummy: TransactionReportFilters = {
    period: "today",
    locationArea: undefined,
    location: undefined,
    device: undefined,
    paymentMethod: undefined,
    staff: undefined
};

const TransactionFiltersContext = React.createContext({
    filters: defaultFiltersDummy,
    unsavedFilters: defaultFiltersDummy,
    defaultFilters: defaultFiltersDummy,
    setFilters: (f: (prevFilters: TransactionReportFilters) => TransactionReportFilters) => { /* no op */
    },
    setFiltersStateless: (f: (prevFilters: TransactionReportFilters) => TransactionReportFilters) => { /* no op */
    },
    validateFilters: (() => false) as () => boolean,
    applyFilters: (() => false) as () => boolean,
    resetFilters: () => { /* no op */
    },
    filtersChanged: false,
    filtersOpen: false,
    openFilters: () => { /* no  op */
    },
    closeFilters: (force?: boolean) => { /* no  op */
    },
    dateRangeConfig: {
        weeksStartOn: "monday" as DayOfWeek,
        daysStartAt: 0,
        useLocalTime: true
    } as DateRangeConfig,
    validationErrors: [] as ValidationError[],
    chips: [] as TableChip[],
    isLoading: true,
    isError: false,
    refetch: () => { /* no op */ }
});

/*
PSA for users of this context:
- We switched from a synchronous to an asynchronous paradigm due to the user preferences on when days and weeks start.
- This means this context now has isLoading, isError and refetch parameters, to handle the required API requests.
- While the filters context is loading, it is better to disregard the values of the context (as it will use defaults and update itself after loading, which will cause a visible flicker).
- So don't make requests until you have the filters loaded. And show to the user we are loading.
- If there is an error in the request, the context will show isError: true. You can call to refetch to make the filters grab the user preferences again.
- You may also just work with the defaults when the preferences fail to load, but this could cause user confusion.
 */
const TransactionFiltersContextProvider = ({children}: { children: React.ReactNode }) => {
    const {isLoadingCalendarPreferences, isErrorCalendarPreferences, calendarPreferences, refetchCalendarPreferences} = useContext(SettingsContext);

    const dateRangeConfig: DateRangeConfig = useMemo(() => ({
        weeksStartOn: isLoadingCalendarPreferences ? "monday" : calendarPreferences.weeksStart as unknown as DayOfWeek,
        daysStartAt: isLoadingCalendarPreferences ? DEFAULT_BUSINESS_START_HOUR : calendarPreferences.daysStart,
        useLocalTime: true
    }), [isLoadingCalendarPreferences, calendarPreferences]);

    const defaultFilters: TransactionReportFilters = useMemo(() => ({
        period: "today",
        periodFrom: dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
        periodTo: dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
        locationArea: undefined,
        location: undefined,
        device: undefined,
        paymentMethod: undefined,
        staff: undefined,
        hasActiveFlag: true
    }), [dateRangeConfig]);

    const {
        val: unsavedFilters,
        setVal: internalSetFilters,
        committed: filters,
        changed: filtersChanged,
        apply: internalApplyFilters,
        reset: internalResetFilters,
        forceSet: forceSetFilters
    } = useSavedField<TransactionReportFilters>(defaultFilters);

    // update default filters when user preferences are loaded
    useEffect(() => {
        forceSetFilters(() => defaultFilters);
    }, [forceSetFilters, defaultFilters]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const {t} = useTranslation();
    const { user } = useContext(UserContext);

    const setFilters = useCallback((f: (prevFilters: TransactionReportFilters) => TransactionReportFilters) => {
        internalSetFilters((prevFilters) => {
            const newFilters = f(prevFilters);
            if (newFilters.period !== prevFilters.period && isPresetPeriod(newFilters.period)) {
                const range = dateRange(newFilters.period, undefined, undefined, undefined, dateRangeConfig);
                return {
                    ...newFilters,
                    periodFrom: range.startDate,
                    periodTo: range.endDate
                };
            }
            if (
                newFilters.period === prevFilters.period &&
                (newFilters.periodFrom !== prevFilters.periodFrom || newFilters.periodTo !== prevFilters.periodTo)
            ) {
                return {
                    ...newFilters,
                    period: "custom"
                }
            }
            return newFilters;
        })
    }, [dateRangeConfig, internalSetFilters]);

    const validateFilters = useCallback(() => {
        const errors = [];

        // date checks
        // we must add 1 extra hour to account for a maximum date range
        // that includes a day where daylight savings happen, and 
        // consequently, 1 extra hour exists inside the range
        const maxDayLimitHours = 31 * 24 + 1;
        const validFrom = !Number.isNaN(Date.parse(unsavedFilters.periodFrom || ""));
        const validTo = !Number.isNaN(Date.parse(unsavedFilters.periodTo || ""));
        let dateGap;

        if (!validFrom) {
            errors.push({field: "periodFrom", message: t("components.datePicker.enterValidDate")});
        }
        if (!validTo) {
            errors.push({field: "periodTo", message: t("components.datePicker.enterValidDate")});
        }
        if (validFrom && validTo) {
            dateGap = new Date(unsavedFilters.periodTo || "").getTime() - new Date(unsavedFilters.periodFrom || "").getTime()
        }
        if (dateGap && dateGap < 0) {
            errors.push({field: "periodFrom", message: t("components.datePicker.startDateMayNotBeAfterEnd")});
        }
        if (dateGap === 0) {
            errors.push({field: "periodTo", message: t("components.datePicker.endDateMayNotBeBeforeStart")});
        }
        // Converts the filter's milliseconds to hours and compares to 31 days worth of hours 
        if (dateGap && (dateGap)/(1000 * 60 * 60) > maxDayLimitHours ){
            errors.push({field: "periodTo", message: t("components.datePicker.dateRangeLimitedToOneMonth")});
        }

        // guid check
        const validCompanyGuid = !unsavedFilters.companyGuid || /[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}/i.test(unsavedFilters.companyGuid);

        if (!validCompanyGuid) {
            errors.push({field: "companyGuid", message: t("components.filters.companyIdInvalid")});
        }

        setValidationErrors(errors);
        return errors.length === 0;
    }, [unsavedFilters, setValidationErrors, t]);

    const applyFilters = useCallback(() => {
        if (!validateFilters()) {
            return false;
        }
        setValidationErrors([]);
        internalApplyFilters();
        return true;
    }, [validateFilters, setValidationErrors, internalApplyFilters]);

    const resetFilters = useCallback(() => {
        setValidationErrors([]);
        internalResetFilters();
    }, [setValidationErrors, internalResetFilters]);

    const openFilters = useCallback(() => {
        setFiltersOpen(true);
    }, [setFiltersOpen]);

    const closeFilters = useCallback((force = false) => {
        if (filtersChanged && !force) {
            setDialogOpen(true);
            return;
        }
        setFiltersOpen(false);
    }, [setDialogOpen, setFiltersOpen, filtersChanged]);

    const resetPeriod = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            period: defaultFilters.period,
            periodFrom: defaultFilters.periodFrom,
            periodTo: defaultFilters.periodTo,
        }));
    }, [forceSetFilters, defaultFilters]);

    const resetGuid = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            companyGuid: undefined
        }));
    }, [forceSetFilters]);

    const resetFlags = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            activeFlagCategory: undefined,
            hasActiveFlag: undefined,
        }));
    }, [forceSetFilters]);

    const resetLocation = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            location: undefined,
        }));
    }, [forceSetFilters]);

    const resetDevice = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            device: undefined,
        }));
    }, [forceSetFilters]);


    const resetStaff = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            staff: undefined,
        }));
    }, [forceSetFilters]);


    const resetLocationArea = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            locationArea: undefined,
        }));
    }, [forceSetFilters]);


    const resetPaymentMethod = useCallback(() => {
        forceSetFilters((prevFilters) => ({
            ...prevFilters,
            paymentMethod: undefined,
        }));
    }, [forceSetFilters]);

    const getLabelForFlag = useCallback((category?: keyof typeof transactionFeedbackOptions | "all" | "allFlagged") => {
        if (category === "all") {
            return t("components.filters.allTransactions");
        }
        if (category !== "allFlagged" && category) {
            return t(`screens.transactions.feedback.options.${category}`);
        }
        return t("components.filters.allFlaggedTransactions");
    }, [t]);

    const context = useMemo(() => {
        const chips = [
            {   
                labelType: 'period',
                label: getLabelForPeriod(filters.period, filters.periodFrom, filters.periodTo, t, user?.locale || EN_GB),
                onDelete: filters.period === defaultFilters.period ? undefined : resetPeriod
            },
            ...(filters.companyGuid ? [{
                labelType: 'companyGuid',
                label: `${t("screens.transactions.fields.companyId")}: ${filters.companyGuid}`,
                onDelete: resetGuid,
                adminView: true
            }] : []),
            ...(filters.activeFlagCategory || filters.hasActiveFlag ? [{
                labelType: 'activeFlagCategory',
                label: `${t("screens.transactions.feedback.title")}: ${getLabelForFlag(filters.activeFlagCategory)}`,
                onDelete: resetFlags,
                adminView: true
            }] : []),
            ...(filters.location ? [{
                labelType: 'location',
                label: `${t("screens.transactions.fields.location")}: ${filters.location.name}`,
                onDelete: resetLocation,
            }] : []),
            ...(filters.device ? [{
                labelType: 'device',
                label: `${t("screens.transactions.fields.device")}: ${filters.device.name}`,
                onDelete: resetDevice,
            }] : []),
            ...(filters.paymentMethod ? [{
                labelType: 'paymentMethod',
                label: `${t("screens.transactions.fields.paymentMethod")}: ${filters.paymentMethod.name}`,
                onDelete: resetPaymentMethod,
            }] : []),
            ...(filters.staff ? [{
                labelType: 'staff',
                label: `${t("screens.transactions.fields.staff")}: ${filters.staff.name}`,
                onDelete: resetStaff,
            }] : []),
            ...(filters.locationArea ? [{
                labelType: 'locationArea',
                label: `${t("screens.transactions.fields.locationArea")}: ${filters.locationArea.name}`,
                onDelete: resetLocationArea,
            }] : []),
        ];
        return {
            filters,
            unsavedFilters,
            setFilters,
            setFiltersStateless: internalSetFilters,
            validateFilters,
            applyFilters,
            resetFilters,
            defaultFilters,
            filtersChanged,
            filtersOpen,
            openFilters,
            closeFilters,
            dateRangeConfig,
            validationErrors,
            chips,
            isLoading: isLoadingCalendarPreferences,
            isError: isErrorCalendarPreferences,
            refetch: refetchCalendarPreferences
        };
    }, [t, isErrorCalendarPreferences, refetchCalendarPreferences, isLoadingCalendarPreferences, defaultFilters, filters, unsavedFilters, setFilters, internalSetFilters, validateFilters, applyFilters, resetFilters, filtersChanged, filtersOpen, openFilters, closeFilters, dateRangeConfig, validationErrors, resetPeriod, getLabelForFlag, resetFlags, resetGuid, resetLocation, resetDevice]);

    return <><TransactionFiltersContext.Provider value={context}>{children}</TransactionFiltersContext.Provider>
        <ConfirmDiscardDialog 
            title={t('components.filters.confirmDiscard.title')} 
            description={t('components.filters.confirmDiscard.message')}
            dialogOpen={dialogOpen} 
            onClose={() => setDialogOpen(false)} 
            onApply={() => {
            if (applyFilters()) setFiltersOpen(false);
        }}
                              onReset={() => {
                                  resetFilters();
                                  setFiltersOpen(false);
                              }}/></>;
}

export {
    TransactionFiltersContext,
    TransactionFiltersContextProvider
}
